import { isPreviewId } from '@actual-app/core/shared/transactions';
import type { TransactionEntity } from '@actual-app/core/types/models';

type Movimiento = Pick<
  TransactionEntity,
  'id' | 'date' | 'amount' | 'is_child' | 'parent_id'
>;

export type SeccionDia<T extends Movimiento> = {
  id: string;
  date: string;
  transactions: T[];
};

/**
 * Reparte los movimientos de la lista (H60): los pagos fijos futuros van
 * aparte (bloque «Próximos») y el resto, una sola sección por día, mezclando
 * los reales con los pagos fijos atrasados, en camino o de hoy. Así «Hoy» o «Ayer» no se
 * repiten aunque los previstos lleguen antes que los reales.
 */
export function agruparPorDia<T extends Movimiento>(
  movimientos: readonly T[],
  hoy: string,
): { proximos: T[]; secciones: SeccionDia<T>[] } {
  const proximos: T[] = [];
  const resto: T[] = [];
  for (const m of movimientos) {
    if (isPreviewId(m.id) && m.date > hoy) {
      proximos.push(m);
    } else {
      resto.push(m);
    }
  }
  // Orden estable por fecha descendente: dentro de un día, el orden de
  // llegada (previstos primero, luego los reales).
  const ordenados = resto
    .map((m, i) => ({ m, i }))
    .sort((a, b) =>
      a.m.date === b.m.date ? a.i - b.i : a.m.date < b.m.date ? 1 : -1,
    )
    .map(({ m }) => m);

  const secciones: SeccionDia<T>[] = [];
  for (const m of ordenados) {
    const ultima = secciones[secciones.length - 1];
    if (ultima && ultima.date === m.date) {
      ultima.transactions.push(m);
    } else {
      secciones.push({ id: m.date, date: m.date, transactions: [m] });
    }
  }
  return { proximos, secciones };
}

/**
 * Movimientos que cuentan como uno (H63): fuera los previstos y las partes
 * de un movimiento dividido cuyo padre también está en la lista (el padre ya
 * lleva el importe total).
 */
function movimientosQueCuentan<T extends Movimiento>(
  movimientos: readonly T[],
) {
  const ids = new Set(movimientos.map(m => m.id));
  return movimientos.filter(
    m =>
      !isPreviewId(m.id) &&
      !(m.is_child && m.parent_id && ids.has(m.parent_id)),
  );
}

/** Cifras de la cabecera de búsqueda: «23 movimientos · −443,45 €». */
export function resumenBusqueda(movimientos: readonly Movimiento[]): {
  cantidad: number;
  total: number;
} {
  const cuentan = movimientosQueCuentan(movimientos);
  return {
    cantidad: cuentan.length,
    total: cuentan.reduce((suma, m) => suma + (m.amount ?? 0), 0),
  };
}

/** Total de los pagos fijos del bloque «Próximos». */
export function totalProximos(proximos: readonly Movimiento[]): number {
  return proximos
    .filter(m => !m.is_child)
    .reduce((suma, m) => suma + (m.amount ?? 0), 0);
}

export const CLAVE_PROXIMOS_ABIERTO = 'rumbo.movimientos.proximosAbierto';

export function leerProximosAbierto(): boolean {
  try {
    return window.localStorage.getItem(CLAVE_PROXIMOS_ABIERTO) === '1';
  } catch {
    return false;
  }
}

export function guardarProximosAbierto(abierto: boolean): void {
  try {
    window.localStorage.setItem(CLAVE_PROXIMOS_ABIERTO, abierto ? '1' : '0');
  } catch {
    // Sin almacenamiento: se queda cerrado la próxima vez.
  }
}

/** `on-way`: pasado el día de cargo, dentro del margen («En camino»). */
export type EstadoPrevisto = 'missed' | 'due' | 'on-way' | 'upcoming';

/** Estado del pago fijo de una fila prevista (forceUpcoming manda). */
export function estadoPrevisto(
  categoria: string | null | undefined,
  forceUpcoming?: boolean,
): EstadoPrevisto | null {
  if (forceUpcoming) return 'upcoming';
  switch (categoria) {
    case 'missed':
    case 'due':
    case 'on-way':
    case 'upcoming':
      return categoria;
    default:
      return null;
  }
}

/**
 * B8 · H13: ¿tocar una fila la selecciona (en vez de abrirla)? Sí con
 * pulsación larga, con el modo «Seleccionar» de la brújula o si ya hay
 * alguna elegida. Los previstos (pagos fijos aún sin crear) nunca.
 */
export function tocarSelecciona({
  esPrevisto,
  pulsacionLarga,
  modoSeleccion,
  seleccionados,
}: {
  esPrevisto: boolean;
  pulsacionLarga: boolean;
  modoSeleccion: boolean;
  seleccionados: number;
}): boolean {
  if (esPrevisto) {
    return false;
  }
  return pulsacionLarga || modoSeleccion || seleccionados > 0;
}
